import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Plus,
  Target,
  ChevronDown,
  ChevronRight,
  Trash2,
  Check,
  X,
  Calendar,
  Loader2,
} from 'lucide-react';
import { NumberField } from '@/cowork/shared/NumberField';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '../api/coworkApi';
import { avatarSrc } from '../avatarSrc';
import type { Agent, OKR } from '../types/cowork';

// ── Agent avatar helper ──────────────────────────────────────────
function AgentAvatar({ agent, size = 16, className }: {
  agent: { avatarEmoji?: string; avatarUrl?: string; name?: string; accentColor?: string };
  size?: number;
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);
  const url = avatarSrc(agent) ?? null;

  if (url && !imgError) {
    return (
      <img
        src={url}
        alt={agent.name || ''}
        className={cn('rounded-full object-cover', className)}
        style={{ width: size, height: size }}
        onError={() => setImgError(true)}
      />
    );
  }
  return <span className={className} style={{ fontSize: size * 0.75, lineHeight: 1 }}>{agent.avatarEmoji || ''}</span>;
}

// ── Progress bar ─────────────────────────────────────────────────
function ProgressBar({ value, height = 6, color }: { value: number; height?: number; color?: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const barColor = color || (pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#6366f1');
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 rounded-full bg-[#f2f4f7] overflow-hidden" style={{ height }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      <span className="text-[11px] font-medium text-[#667085] whitespace-nowrap">{pct}%</span>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
export function OKRPage({ agents, brandId, onBack }: {
  agents: Agent[];
  brandId?: string;
  onBack: () => void;
}) {
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAddObjective, setShowAddObjective] = useState(false);
  const [addingKrForId, setAddingKrForId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchOkrs = useCallback(async () => {
    try {
      const { okrs: data } = await coworkApi.okrs.list(brandId);
      setOkrs(data);
      // Auto-expand objectives that have key results
      const expanded = new Set<string>();
      data.forEach(o => {
        if (o.type === 'objective' && o.keyResults && o.keyResults.length > 0) {
          expanded.add(o.id);
        }
      });
      setExpandedIds(prev => {
        // Preserve existing expansions, add new ones with KRs
        const merged = new Set(prev);
        expanded.forEach(id => merged.add(id));
        return merged;
      });
    } catch (err) {
      console.error('[OKRPage] Failed to fetch OKRs:', err);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetchOkrs(); }, [fetchOkrs]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await coworkApi.okrs.delete(id);
      await fetchOkrs();
    } catch (err) {
      console.error('[OKRPage] Delete failed:', err);
    }
  };

  const handleUpdateProgress = async (id: string, currentValue: number) => {
    try {
      await coworkApi.okrs.updateProgress(id, currentValue);
      await fetchOkrs();
    } catch (err) {
      console.error('[OKRPage] Progress update failed:', err);
    }
  };

  const objectives = okrs.filter(o => o.type === 'objective');

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#eaecf0]">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 rounded-md hover:bg-[#f9fafb] transition-colors">
            <ArrowLeft className="w-4 h-4 text-[#667085]" />
          </button>
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#6366f1]" />
            <h1 className="text-[16px] font-semibold text-[#101828]">OKRs</h1>
            <span className="text-[13px] text-[#667085]">Objectives & Key Results</span>
          </div>
        </div>
        <button
          onClick={() => setShowAddObjective(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#6366f1] text-white text-[12px] font-medium hover:bg-[#4f46e5] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Objective
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-5 h-5 text-[#667085] animate-spin" />
          </div>
        )}

        {!loading && objectives.length === 0 && !showAddObjective && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Target className="w-10 h-10 text-[#d0d5dd] mb-3" />
            <p className="text-[14px] text-[#667085] mb-1">No objectives yet</p>
            <p className="text-[12px] text-[#98a2b3] mb-4">Create your first objective to start tracking progress</p>
            <button
              onClick={() => setShowAddObjective(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#6366f1] text-white text-[12px] font-medium hover:bg-[#4f46e5] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Objective
            </button>
          </div>
        )}

        {/* Add Objective Form */}
        {showAddObjective && (
          <AddOkrForm
            type="objective"
            agents={agents}
            brandId={brandId}
            onSave={async () => { setShowAddObjective(false); await fetchOkrs(); }}
            onCancel={() => setShowAddObjective(false)}
          />
        )}

        {/* Objectives List */}
        <div className="space-y-3">
          {objectives.map(obj => (
            <div key={obj.id} className="border border-[#eaecf0] rounded-xl overflow-hidden">
              {/* Objective Header */}
              <div className="px-4 py-3 bg-[#f9fafb]">
                {editingId === obj.id ? (
                  <InlineEditForm
                    okr={obj}
                    agents={agents}
                    onSave={async () => { setEditingId(null); await fetchOkrs(); }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleExpand(obj.id)}
                      className="mt-0.5 p-0.5 rounded hover:bg-[#eaecf0] transition-colors"
                    >
                      {expandedIds.has(obj.id) ? (
                        <ChevronDown className="w-4 h-4 text-[#667085]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[#667085]" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          onClick={() => setEditingId(obj.id)}
                          className="text-[14px] font-semibold text-[#101828] hover:text-[#6366f1] transition-colors text-left"
                        >
                          {obj.title}
                        </button>
                        {obj.status === 'completed' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#ecfdf3] text-[#027a48] font-medium">
                            Completed
                          </span>
                        )}
                      </div>
                      {obj.description && (
                        <p className="text-[12px] text-[#667085] mb-2">{obj.description}</p>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[300px]">
                          <ProgressBar value={obj.progress} />
                        </div>
                        {obj.ownerAgent && (
                          <div className="flex items-center gap-1.5">
                            <AgentAvatar agent={obj.ownerAgent} size={18} />
                            <span className="text-[11px] text-[#667085]">{obj.ownerAgent.name}</span>
                          </div>
                        )}
                        {obj.dueDate && (
                          <div className="flex items-center gap-1 text-[11px] text-[#667085]">
                            <Calendar className="w-3 h-3" />
                            {new Date(obj.dueDate).toLocaleDateString()}
                          </div>
                        )}
                        <span className="text-[11px] text-[#98a2b3]">
                          {obj.keyResults?.length || 0} KR{(obj.keyResults?.length || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(obj.id)}
                      className="p-1 rounded hover:bg-[#fee2e2] text-[#98a2b3] hover:text-[#ef4444] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Key Results */}
              {expandedIds.has(obj.id) && (
                <div className="px-4 py-2 space-y-2">
                  {obj.keyResults?.map(kr => (
                    <div key={kr.id} className="flex items-start gap-3 pl-7 py-2 border-b border-[#f2f4f7] last:border-0">
                      {editingId === kr.id ? (
                        <div className="flex-1">
                          <InlineEditForm
                            okr={kr}
                            agents={agents}
                            onSave={async () => { setEditingId(null); await fetchOkrs(); }}
                            onCancel={() => setEditingId(null)}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => setEditingId(kr.id)}
                              className="text-[13px] font-medium text-[#344054] hover:text-[#6366f1] transition-colors text-left"
                            >
                              {kr.title}
                            </button>
                            <div className="flex items-center gap-3 mt-1.5">
                              <div className="flex-1 max-w-[250px]">
                                <ProgressBar value={kr.progress} height={4} />
                              </div>
                              {kr.targetValue !== undefined && kr.targetValue !== null && (
                                <span className="text-[11px] text-[#667085]">
                                  {kr.currentValue ?? 0} / {kr.targetValue} {kr.metricUnit || ''}
                                </span>
                              )}
                              {kr.ownerAgent && (
                                <div className="flex items-center gap-1">
                                  <AgentAvatar agent={kr.ownerAgent} size={14} />
                                  <span className="text-[10px] text-[#667085]">{kr.ownerAgent.name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {kr.targetValue !== undefined && kr.targetValue !== null && (
                            <QuickProgressInput
                              currentValue={kr.currentValue ?? 0}
                              onSave={(val) => handleUpdateProgress(kr.id, val)}
                            />
                          )}
                          <button
                            onClick={() => handleDelete(kr.id)}
                            className="p-1 rounded hover:bg-[#fee2e2] text-[#98a2b3] hover:text-[#ef4444] transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Add KR button / form */}
                  {addingKrForId === obj.id ? (
                    <div className="pl-7">
                      <AddOkrForm
                        type="key_result"
                        parentId={obj.id}
                        agents={agents}
                        brandId={brandId}
                        onSave={async () => { setAddingKrForId(null); await fetchOkrs(); }}
                        onCancel={() => setAddingKrForId(null)}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingKrForId(obj.id)}
                      className="flex items-center gap-1.5 pl-7 py-1.5 text-[12px] text-[#6366f1] hover:text-[#4f46e5] font-medium transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Add Key Result
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quick progress input ─────────────────────────────────────────
function QuickProgressInput({ currentValue, onSave }: { currentValue: number; onSave: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(currentValue));

  if (!editing) {
    return (
      <button
        onClick={() => { setVal(String(currentValue)); setEditing(true); }}
        className="text-[11px] px-1.5 py-0.5 rounded bg-[#f2f4f7] text-[#667085] hover:bg-[#eaecf0] transition-colors"
        title="Update progress"
      >
        Update
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <NumberField
        step={0.1}
        decimals={2}
        min={-1e12}
        allowEmpty
        value={val === '' ? null : Number(val)}
        onChange={n => setVal(n == null ? '' : String(n))}
        boxClassName="relative flex w-16 items-center"
        className="w-full px-1.5 py-0.5 text-[11px] border border-[#d0d5dd] rounded focus:outline-none focus:border-[#be43ff]"
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') { onSave(Number(val)); setEditing(false); }
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <button onClick={() => { onSave(Number(val)); setEditing(false); }} className="p-0.5 text-[#10b981] hover:text-[#059669]">
        <Check className="w-3 h-3" />
      </button>
      <button onClick={() => setEditing(false)} className="p-0.5 text-[#98a2b3] hover:text-[#667085]">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ── Add OKR form ─────────────────────────────────────────────────
function AddOkrForm({ type, parentId, agents, brandId, onSave, onCancel }: {
  type: 'objective' | 'key_result';
  parentId?: string;
  agents: Agent[];
  brandId?: string;
  onSave: () => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerAgentId, setOwnerAgentId] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [metricUnit, setMetricUnit] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await coworkApi.okrs.create({
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        parentId,
        ownerAgentId: ownerAgentId || undefined,
        targetValue: targetValue ? Number(targetValue) : undefined,
        metricUnit: metricUnit || undefined,
        dueDate: dueDate || undefined,
        brandId,
      });
      await onSave();
    } catch (err) {
      console.error('[OKRPage] Create failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#d0d5dd] rounded-lg p-3 mb-3 bg-white">
      <div className="space-y-2">
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={type === 'objective' ? 'Objective title...' : 'Key result title...'}
          className="w-full px-3 py-1.5 text-[13px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleSubmit(); if (e.key === 'Escape') onCancel(); }}
        />
        {type === 'objective' && (
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="w-full px-3 py-1.5 text-[12px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
          />
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={ownerAgentId}
            onChange={e => setOwnerAgentId(e.target.value)}
            className="px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] text-[#344054] bg-white"
          >
            <option value="">No owner</option>
            {agents.filter(a => a.isActive).map(a => (
              <option key={a.id} value={a.id}>{a.avatarEmoji} {a.name}</option>
            ))}
          </select>
          {type === 'key_result' && (
            <>
              <NumberField
                step={0.1}
                decimals={2}
                allowEmpty
                value={targetValue === '' ? null : Number(targetValue)}
                onChange={n => setTargetValue(n == null ? '' : String(n))}
                placeholder="Target"
                boxClassName="relative flex w-20 items-center"
                className="w-full px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
              />
              <input
                type="text"
                value={metricUnit}
                onChange={e => setMetricUnit(e.target.value)}
                placeholder="Unit (e.g., %)"
                className="w-24 px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
              />
            </>
          )}
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] text-[#344054]"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          onClick={onCancel}
          className="px-3 py-1 text-[12px] text-[#667085] hover:text-[#344054] transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!title.trim() || saving}
          className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#6366f1] text-white text-[12px] font-medium hover:bg-[#4f46e5] disabled:opacity-50 transition-colors"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          {type === 'objective' ? 'Create Objective' : 'Add Key Result'}
        </button>
      </div>
    </div>
  );
}

// ── Inline edit form ─────────────────────────────────────────────
function InlineEditForm({ okr, agents, onSave, onCancel }: {
  okr: OKR;
  agents: Agent[];
  onSave: () => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(okr.title);
  const [description, setDescription] = useState(okr.description || '');
  const [ownerAgentId, setOwnerAgentId] = useState(okr.ownerAgentId || '');
  const [status, setStatus] = useState(okr.status);
  const [dueDate, setDueDate] = useState(okr.dueDate || '');
  const [targetValue, setTargetValue] = useState(String(okr.targetValue ?? ''));
  const [metricUnit, setMetricUnit] = useState(okr.metricUnit || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await coworkApi.okrs.update(okr.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        ownerAgentId: ownerAgentId || undefined,
        status,
        dueDate: dueDate || undefined,
        targetValue: targetValue ? Number(targetValue) : undefined,
        metricUnit: metricUnit || undefined,
      } as Partial<OKR>);
      await onSave();
    } catch (err) {
      console.error('[OKRPage] Update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        className="w-full px-2 py-1 text-[13px] font-semibold border border-[#d0d5dd] rounded-lg focus:outline-none focus:border-[#be43ff]"
        autoFocus
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onCancel(); }}
      />
      {okr.type === 'objective' && (
        <input
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Description"
          className="w-full px-2 py-1 text-[12px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
        />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={ownerAgentId}
          onChange={e => setOwnerAgentId(e.target.value)}
          className="px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] text-[#344054] bg-white"
        >
          <option value="">No owner</option>
          {agents.filter(a => a.isActive).map(a => (
            <option key={a.id} value={a.id}>{a.avatarEmoji} {a.name}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={e => setStatus(e.target.value as OKR['status'])}
          className="px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] text-[#344054] bg-white"
        >
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
        {okr.type === 'key_result' && (
          <>
            <NumberField
              step={0.1}
              decimals={2}
              allowEmpty
              value={targetValue === '' ? null : Number(targetValue)}
              onChange={n => setTargetValue(n == null ? '' : String(n))}
              placeholder="Target"
              boxClassName="relative flex w-20 items-center"
              className="w-full px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
            />
            <input
              type="text"
              value={metricUnit}
              onChange={e => setMetricUnit(e.target.value)}
              placeholder="Unit"
              className="w-20 px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] placeholder:text-[#98a2b3]"
            />
          </>
        )}
        <input
          type="date"
          value={dueDate}
          onChange={e => setDueDate(e.target.value)}
          className="px-2 py-1 text-[11px] border border-[#eaecf0] rounded-lg focus:outline-none focus:border-[#be43ff] text-[#344054]"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-2 py-1 text-[11px] text-[#667085] hover:text-[#344054]">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!title.trim() || saving}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#6366f1] text-white text-[11px] font-medium hover:bg-[#4f46e5] disabled:opacity-50 transition-colors"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          Save
        </button>
      </div>
    </div>
  );
}
